import { createApp } from 'vue'
import axios from 'axios'
import VueAxios from 'vue-axios'
import { useCookies } from "vue3-cookies"
import store from '@/store/index'
import { LANGUAGE } from '@/const/language'
import { SETTING_NAVBAR_MODE, SETTING_SIDEBAR_MODE } from '@/const/cookie'
import { CHANGE_COUNTRY } from '@/const/store'
import { v4 as uuidv4 } from 'uuid'
import i18n from '../../createI18n'
import commons from '@/utils/Common'
import messageShow from '@/utils/MessageShow'
import router from '@/router'
import { CONFIG_API } from '@/config/app'
import { LOGIN } from '@/const/path'

const app = createApp({})
app.use(VueAxios, axios)
app.use(i18n)

let interceptorsReady = false

const parseToken = (tokenUser: any) => {
    if (tokenUser === null || tokenUser === undefined || tokenUser === '') {
        return null
    }
    try {
        const token = typeof tokenUser === 'string' ? JSON.parse(tokenUser) : tokenUser
        if (!token || typeof token !== 'object') return null
        if (!token.accessToken) return null
        return token
    } catch {
        return null
    }
}

const applyAuthHeaders = (headers: Record<string, any> = app.axios.defaults.headers.common) => {
    const token = parseToken(commons.getToken())
    if (token) {
        headers['Authorization'] = `${token.tokenType || 'Bearer'} ${token.accessToken}`
        if (token.refreshToken) {
            headers['RefreshToken'] = token.refreshToken
        }
        if (token.sessionId) {
            headers['session'] = token.sessionId
        }
        store.dispatch('setLoggedIn', true)
        return true
    }

    delete headers['Authorization']
    delete headers['RefreshToken']
    delete headers['session']
    store.dispatch('setLoggedIn', false)
    return false
}

const setupInterceptorsOnce = () => {
    if (interceptorsReady) return
    interceptorsReady = true

    // Always attach latest token + tenant per request
    app.axios.interceptors.request.use((config) => {
        config.headers = config.headers || {}
        const tenantCode = commons.getTenantCode?.() || commons.ensureTenant?.()
        if (tenantCode) {
            config.headers['X-Tenant-Code'] = tenantCode
        }
        const token = parseToken(commons.getToken())
        if (token) {
            config.headers['Authorization'] = `${token.tokenType || 'Bearer'} ${token.accessToken}`
            if (token.refreshToken) {
                config.headers['RefreshToken'] = token.refreshToken
            }
            if (token.sessionId) {
                config.headers['session'] = token.sessionId
            }
        }
        return config
    })

    app.axios.interceptors.response.use(
        (response) => response,
        (error) => {
            const res = error.response
            if (!res) return Promise.reject(error)

            if (res.status === 401 || res.status === 403) {
                messageShow.error('Error ' + res.status, 'Authoration')
                commons.loginErrorAuth()
                router.push(LOGIN)
            }

            return Promise.reject(error)
        }
    )
}

const service = {
    /** Apply token from storage into axios defaults (call right after login) */
    applyAuth() {
        setupInterceptorsOnce()
        return applyAuthHeaders()
    },
    init() {
        app.axios.defaults.baseURL = process.env.VUE_APP_BASE_API
        app.axios.defaults.headers.common['Content-Type'] = 'application/json'
        app.axios.defaults.headers.common['X-Trace-Id'] = uuidv4()

        const { cookies } = useCookies()
        const langCookie = cookies.get(LANGUAGE)
        if (langCookie != null) {
            store.commit(CHANGE_COUNTRY, langCookie)
        }
        app.axios.defaults.headers.common['Accept-Language'] = langCookie

        applyAuthHeaders()

        const settingNavbarMode = cookies.get(SETTING_NAVBAR_MODE)
        if (settingNavbarMode !== null) {
            store.dispatch('setNavbarVariant', settingNavbarMode)
        }
        const settingSidebarMode = cookies.get(SETTING_SIDEBAR_MODE)
        if (settingSidebarMode !== null) {
            store.dispatch('setSidebarSkin', settingSidebarMode)
        }

        setupInterceptorsOnce()
    },
    get(resource: any, params: any) {
        return app.axios.get(resource, params).catch(error => {
            throw error
        })
    },
    post(resource: any, params: any) {
        return app.axios.post(`${resource}`, params).catch(error => {
            throw error
        })
    },
    patch(resource: any, params: any) {
        return app.axios.patch(`${resource}`, params).catch(error => {
            throw error
        })
    },
    getNoParam(resource: any) {
        return app.axios.get(resource).catch(error => {
            throw error
        })
    },
    getFileNoParam(resource: any) {
        app.axios.defaults.responseType = 'blob'
        return app.axios.get(resource).catch(error => {
            throw error
        })
    },
    postNoParam(resource: any) {
        return app.axios.post(`${resource}`).catch(error => {
            throw error
        })
    },
    uploadFile(resource: any, params: any) {
        app.axios.defaults.headers.common['Content-Type'] = 'multipart/form-data'
        app.axios.defaults.headers.common['s-merchant-id'] = 'TQ2D3BTWQYF3fS5uNmk07JG8RT4w8m6CIbttrYaYN0Q'
        return app.axios.post(`${resource}`, params).catch(error => {
            throw error
        })
    },
    deleteNoParam(resource: any) {
        return app.axios.delete(`${resource}`).catch(error => {
            throw error
        })
    },
    /** GET against VUE_APP_CONFIG_API (does not change main baseURL) */
    getConfig(resource: any, params?: any) {
        setupInterceptorsOnce()
        return app.axios
            .get(resource, {
                ...(params || {}),
                baseURL: CONFIG_API || process.env.VUE_APP_CONFIG_API,
            })
            .catch((error) => {
                throw error
            })
    },
    /** POST against VUE_APP_CONFIG_API */
    postConfig(resource: any, params?: any) {
        setupInterceptorsOnce()
        return app.axios
            .post(resource, params, {
                baseURL: CONFIG_API || process.env.VUE_APP_CONFIG_API,
            })
            .catch((error) => {
                throw error
            })
    },
}
export default service
